import { useState } from "react";

import { Box, TextField, Typography } from "@mui/material";

import { TfcBannerBox } from "components/TotalFreightCost";
import { MAX_TFC_NOTES_LENGTH } from "constants/index";

import { TfcEditableTable } from "./TfcEditableTable";
import { TfcSubmitButton } from "./TfcSubmitButton";

import type { KeyedMutator } from "swr";

import type { EditableLineItem } from "./TfcEditableTable";

interface TfcSubmitFormProps {
  tfcData: Extract<TfcResponse, { exists: false }>;
  shipment: CarrierCondensedShipment;
  onTfcCreated: KeyedMutator<TfcResponse>;
}

export function TfcSubmitForm(props: TfcSubmitFormProps) {
  const { tfcData, shipment, onTfcCreated } = props;

  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);
  const [note, setNote] = useState("");

  const currency = tfcData.currency ?? "";

  const hasIncompleteItem = lineItems.some(
    (item) => !item.description.trim() || item.line_total_cents === 0,
  );

  return (
    <Box>
      <TfcBannerBox borderColor="#1976D2" backgroundColor="#E3F2FD">
        Enter your total freight costs, excluding tax, below and submit to the
        shipper for confirmation. Once confirmed, you will be able to send a
        summary to your accounting team.
      </TfcBannerBox>

      <TfcEditableTable
        bookedPriceCents={tfcData.booked_price_cents}
        currency={currency}
        lineItems={lineItems}
        onLineItemsChange={setLineItems}
      />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 500, mb: 1 }}>
          Notes (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Add any context for these charges..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: MAX_TFC_NOTES_LENGTH }}
          helperText={`${note.length}/${MAX_TFC_NOTES_LENGTH} characters`}
        />
      </Box>

      <TfcSubmitButton
        shipmentId={shipment.info.id}
        shipperCompanyName={shipment.shipment_company.name}
        lineItems={lineItems}
        note={note}
        bookedPriceCents={tfcData.booked_price_cents}
        currency={currency}
        disabled={hasIncompleteItem}
        onSuccess={() => {
          onTfcCreated();
        }}
      />
    </Box>
  );
}
